import React, { useEffect, useMemo, useState } from 'react';
import { Card, Button, Form, Row, Col, Table, Spinner, Alert, Badge } from 'react-bootstrap';
import { uploadInvoice, fetchInvoices, fetchInvoiceStats } from '../http/invoiceAPI';
import { fetchCounterparties, createCounterparty, fetchCounterpartyDebts, addCounterpartyPayment, updateCounterpartyDebt } from '../http/counterpartyAPI';
import { getImageUrl } from '../utils/imageUtils';
import InvoiceImportModal from './InvoiceImportModal';
import InvoiceOcrModal from './InvoiceOcrModal';

const typeLabels = {
  shoe: 'Обувь',
  clothes: 'Одежда',
  toy: 'Игрушки',
  other: 'Прочее'
};

const monthNames = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];

const InvoicesStorage = () => {
  const today = new Date();
  const [form, setForm] = useState({ type: 'other', amount: '', date: today.toISOString().slice(0,10), files: [], counterpartyId: '' });
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ type: '', year: today.getFullYear(), month: '' });
  const [stats, setStats] = useState({ yearTotal: 0, monthly: [] });
  const [importingInvoice, setImportingInvoice] = useState(null);
  const [ocrInvoiceId, setOcrInvoiceId] = useState(null);
  const [counterparties, setCounterparties] = useState([]);
  const [counterpartyLoading, setCounterpartyLoading] = useState(false);
  const [counterpartyError, setCounterpartyError] = useState('');
  const [newCounterparty, setNewCounterparty] = useState({ name: '', phone: '', notes: '' });
  const [debts, setDebts] = useState([]);
  const [debtsLoading, setDebtsLoading] = useState(false);
  const [debtsError, setDebtsError] = useState('');
  const [paymentInputs, setPaymentInputs] = useState({});
  const [debtInputs, setDebtInputs] = useState({});

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchInvoices(filters);
      setList(Array.isArray(data) ? data : []);
      const statsData = await fetchInvoiceStats(filters.year);
      setStats(statsData || { yearTotal: 0, monthly: [] });
    } catch (e) {
      setError('Ошибка загрузки накладных');
    } finally {
      setLoading(false);
    }
  };

  const loadCounterparties = async () => {
    setCounterpartyLoading(true);
    setCounterpartyError('');
    try {
      const data = await fetchCounterparties();
      setCounterparties(Array.isArray(data) ? data : []);
    } catch (e) {
      setCounterpartyError('Ошибка загрузки контрагентов');
    } finally {
      setCounterpartyLoading(false);
    }
  };

  const loadDebts = async () => {
    setDebtsLoading(true);
    setDebtsError('');
    try {
      const data = await fetchCounterpartyDebts();
      setDebts(Array.isArray(data) ? data : []);
    } catch (e) {
      setDebtsError('Ошибка загрузки долгов по контрагентам');
    } finally {
      setDebtsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    loadCounterparties();
    loadDebts();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0 || !form.date || !form.type || !form.files?.length) {
      setError('Заполните тип, сумму, дату и добавьте файлы');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await uploadInvoice(
        {
          type: form.type,
          amount: form.amount,
          date: form.date,
          counterpartyId: form.counterpartyId || undefined
        },
        form.files
      );
      setForm({ ...form, amount: '', files: [], counterpartyId: '' });
      await Promise.all([loadData(), loadDebts()]);
    } catch (e) {
      setError('Ошибка при загрузке накладных');
    } finally {
      setLoading(false);
    }
  };

  const groupedByMonth = useMemo(() => {
    const map = new Map();
    list.forEach(inv => {
      const key = `${inv.year}-${String(inv.month).padStart(2,'0')}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(inv);
    });
    return Array.from(map.entries()).sort((a,b)=> b[0].localeCompare(a[0]));
  }, [list]);

  const monthlyTotal = (monthKey) => {
    const items = groupedByMonth.find(([k])=>k===monthKey)?.[1] || [];
    return items.reduce((s,i)=> s + Number(i.amount||0), 0);
  };

  const renderImages = (images) => {
    let arr = [];
    try { arr = JSON.parse(images || '[]'); } catch (_) {}
    if (!Array.isArray(arr) || arr.length === 0) return <span className="text-muted">Нет файлов</span>;
    return (
      <div className="d-flex flex-wrap gap-2">
        {arr.map((img, idx) => (
          <a key={idx} href={getImageUrl(img)} target="_blank" rel="noreferrer" className="text-decoration-none">
            <img src={getImageUrl(img)} alt="invoice" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee' }} />
          </a>
        ))}
      </div>
    );
  };

  const handleCreateCounterparty = async (e) => {
    e.preventDefault();
    const trimmedName = newCounterparty.name.trim();
    if (!trimmedName) {
      setCounterpartyError('Введите название контрагента');
      return;
    }
    setCounterpartyLoading(true);
    setCounterpartyError('');
    try {
      await createCounterparty({
        name: trimmedName,
        phone: newCounterparty.phone || undefined,
        notes: newCounterparty.notes || undefined
      });
      setNewCounterparty({ name: '', phone: '', notes: '' });
      await Promise.all([loadCounterparties(), loadDebts()]);
    } catch (e) {
      setCounterpartyError('Ошибка при создании контрагента');
    } finally {
      setCounterpartyLoading(false);
    }
  };

  const handlePaymentChange = (counterpartyId, value) => {
    setPaymentInputs(prev => ({ ...prev, [counterpartyId]: value }));
  };

  const handleDebtChange = (counterpartyId, value) => {
    setDebtInputs(prev => ({ ...prev, [counterpartyId]: value }));
  };

  const handleSaveRow = async (counterpartyId) => {
    const rawPayment = paymentInputs[counterpartyId];
    const paymentAmount = rawPayment !== undefined && rawPayment !== '' ? Number(rawPayment) : 0;

    const rawDebt = debtInputs[counterpartyId];
    const hasDebtEdit = rawDebt !== undefined && rawDebt !== '';
    const desiredDebt = hasDebtEdit ? Number(rawDebt) : null;

    if ((rawPayment && (Number.isNaN(paymentAmount) || paymentAmount <= 0)) ||
        (hasDebtEdit && Number.isNaN(desiredDebt))) {
      setDebtsError('Введите корректные значения для долга и оплаты');
      return;
    }

    setDebtsLoading(true);
    setDebtsError('');
    try {
      if (hasDebtEdit) {
        await updateCounterpartyDebt(counterpartyId, { debt: desiredDebt });
      }
      if (paymentAmount > 0) {
        await addCounterpartyPayment(counterpartyId, { amount: paymentAmount });
      }
      setPaymentInputs(prev => ({ ...prev, [counterpartyId]: '' }));
      setDebtInputs(prev => ({ ...prev, [counterpartyId]: '' }));
      await loadDebts();
    } catch (e) {
      setDebtsError('Ошибка при сохранении изменений по контрагенту');
    } finally {
      setDebtsLoading(false);
    }
  };

  return (
    <Card>
      <Card.Body>
        <div className="d-flex justify-content-between flex-wrap gap-2 mb-3 align-items-center">
          <h3 className="mb-0">Хранилище накладных</h3>
          <div className="d-flex gap-2">
            <Button variant="outline-primary" onClick={loadData} disabled={loading}>Обновить</Button>
          </div>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        {/* Блок работы с контрагентами */}
        <Card className="mb-3">
          <Card.Body>
            <h5 className="mb-3">Контрагенты</h5>
            {counterpartyError && <Alert variant="danger" className="mb-2">{counterpartyError}</Alert>}
            <Form onSubmit={handleCreateCounterparty} className="mb-3">
              <Row className="g-2">
                <Col md={4} sm={6} xs={12}>
                  <Form.Label>Название контрагента</Form.Label>
                  <Form.Control
                    value={newCounterparty.name}
                    onChange={e => setNewCounterparty({ ...newCounterparty, name: e.target.value })}
                    placeholder="Например, ООО «Поставщик»"
                  />
                </Col>
                <Col md={3} sm={6} xs={12}>
                  <Form.Label>Телефон</Form.Label>
                  <Form.Control
                    value={newCounterparty.phone}
                    onChange={e => setNewCounterparty({ ...newCounterparty, phone: e.target.value })}
                    placeholder="+7..."
                  />
                </Col>
                <Col md={3} sm={12} xs={12}>
                  <Form.Label>Комментарий</Form.Label>
                  <Form.Control
                    value={newCounterparty.notes}
                    onChange={e => setNewCounterparty({ ...newCounterparty, notes: e.target.value })}
                    placeholder="Доп. информация"
                  />
                </Col>
                <Col md={2} sm={12} xs={12} className="d-flex align-items-end">
                  <Button type="submit" variant="outline-primary" className="w-100" disabled={counterpartyLoading}>
                    Добавить контрагента
                  </Button>
                </Col>
              </Row>
            </Form>

            {/* Таблица долгов по контрагентам */}
            <h6 className="mb-2">Долги по контрагентам</h6>
            {debtsError && <Alert variant="danger" className="mb-2">{debtsError}</Alert>}
            {debtsLoading ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
              </div>
            ) : debts.length === 0 ? (
              <div className="text-muted">Пока нет контрагентов или долгов</div>
            ) : (
              <div className="table-responsive">
                <Table bordered size="sm" className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Контрагент</th>
                      <th>Долг, ₽</th>
                      <th>Оплатил, ₽</th>
                      <th>Остаток, ₽</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {debts.map(row => {
                      const paymentInput = paymentInputs[row.id] || '';
                      const debtInput = debtInputs[row.id];
                      const effectiveDebt = debtInput !== undefined && debtInput !== ''
                        ? (Number(debtInput) || 0)
                        : Number(row.debt || 0);
                      const plannedPayment = Number(paymentInput) || 0;
                      const rest = Math.max(0, effectiveDebt - plannedPayment);
                      return (
                        <tr key={row.id}>
                          <td>
                            <div className="fw-semibold">{row.name}</div>
                            {row.phone && <div className="text-muted small">{row.phone}</div>}
                          </td>
                          <td style={{ maxWidth: 160 }}>
                            <Form.Control
                              type="number"
                              min="0"
                              step="0.01"
                              value={debtInput !== undefined ? debtInput : Number(row.debt || 0).toFixed(2)}
                              onChange={e => handleDebtChange(row.id, e.target.value)}
                              size="sm"
                            />
                          </td>
                          <td style={{ maxWidth: 160 }}>
                            <Form.Control
                              type="number"
                              min="0"
                              step="0.01"
                              value={paymentInput}
                              onChange={e => handlePaymentChange(row.id, e.target.value)}
                              size="sm"
                            />
                          </td>
                          <td>{rest.toFixed(2)}</td>
                          <td>
                            <Button
                              size="sm"
                              variant="success"
                              onClick={() => handleSaveRow(row.id)}
                              disabled={debtsLoading}
                            >
                              Сохранить
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        <Form onSubmit={handleSubmit} className="mb-4">
          <Row className="g-2">
            <Col md={3} sm={6} xs={12}>
              <Form.Label>Тип товара</Form.Label>
              <Form.Select value={form.type} onChange={e=>setForm({...form,type:e.target.value})}>
                <option value="shoe">Обувь</option>
                <option value="clothes">Одежда</option>
                <option value="toy">Игрушки</option>
                <option value="other">Прочее</option>
              </Form.Select>
            </Col>
            <Col md={3} sm={6} xs={12}>
              <Form.Label>Сумма закупки</Form.Label>
              <Form.Control type="number" min="0" step="0.01" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} />
            </Col>
            <Col md={3} sm={6} xs={12}>
              <Form.Label>Дата</Form.Label>
              <Form.Control type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})} />
            </Col>
            <Col md={3} sm={6} xs={12}>
              <Form.Label>Контрагент</Form.Label>
              <Form.Select
                value={form.counterpartyId}
                onChange={e => setForm({ ...form, counterpartyId: e.target.value })}
                disabled={counterpartyLoading && counterparties.length === 0}
              >
                <option value="">Без контрагента</option>
                {counterparties.map(cp => (
                  <option key={cp.id} value={cp.id}>{cp.name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3} sm={6} xs={12}>
              <Form.Label>Файлы (фото/сканы)</Form.Label>
              <Form.Control multiple type="file" accept="image/*,application/pdf" onChange={e=>setForm({...form,files:Array.from(e.target.files||[])})} />
            </Col>
            <Col xs={12} className="d-flex justify-content-end mt-2">
              <Button type="submit" variant="primary" disabled={loading}>Загрузить накладные</Button>
            </Col>
          </Row>
        </Form>

        <Card className="mb-3">
          <Card.Body>
            <Row className="g-2 align-items-end">
              <Col md={3} sm={6} xs={12}>
                <Form.Label>Тип</Form.Label>
                <Form.Select value={filters.type} onChange={e=>setFilters({...filters,type:e.target.value})}>
                  <option value="">Все типы</option>
                  <option value="shoe">Обувь</option>
                  <option value="clothes">Одежда</option>
                  <option value="toy">Игрушки</option>
                  <option value="other">Прочее</option>
                </Form.Select>
              </Col>
              <Col md={3} sm={6} xs={12}>
                <Form.Label>Год</Form.Label>
                <Form.Control type="number" value={filters.year} onChange={e=>setFilters({...filters,year:e.target.value})} />
              </Col>
              <Col md={3} sm={6} xs={12}>
                <Form.Label>Месяц</Form.Label>
                <Form.Select value={filters.month} onChange={e=>setFilters({...filters,month:e.target.value})}>
                  <option value="">Все</option>
                  {monthNames.map((m,idx)=>(<option key={idx} value={idx+1}>{m}</option>))}
                </Form.Select>
              </Col>
              <Col md={3} sm={6} xs={12} className="d-flex gap-2">
                <Button variant="primary" className="w-100" onClick={loadData} disabled={loading}>Применить</Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Body>
            <h5 className="mb-3">Итог по году {filters.year}</h5>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <Badge bg="primary" className="p-2">Год: {Number(stats.yearTotal||0).toFixed(2)} ₽</Badge>
              <div className="d-flex flex-wrap gap-2">
                {(stats.monthly||[]).map(m => (
                  <Badge key={m.month} bg="light" text="dark" className="p-2">
                    {monthNames[m.month-1]}: {Number(m.total||0).toFixed(2)} ₽
                  </Badge>
                ))}
              </div>
            </div>
          </Card.Body>
        </Card>

        {loading ? (
          <div className="text-center py-4"><Spinner animation="border" /></div>
        ) : (
          groupedByMonth.length === 0 ? (
            <div className="text-muted">Нет накладных</div>
          ) : (
            groupedByMonth.map(([key, items]) => {
              const [y,m] = key.split('-');
              return (
                <Card key={key} className="mb-3">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <div><strong>{monthNames[Number(m)-1]}</strong> {y}</div>
                    <div className="d-flex align-items-center gap-3">
                      <div className="fw-bold">Сумма: {monthlyTotal(key).toFixed(2)} ₽</div>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="table-responsive d-none d-md-block">
                      <Table bordered hover size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th>Дата</th>
                            <th>Тип</th>
                            <th>Контрагент</th>
                            <th>Сумма</th>
                            <th>Файлы</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map(inv => (
                            <tr key={inv.id}>
                              <td>{inv.date}</td>
                              <td>{typeLabels[inv.type] || inv.type}</td>
                            <td>{inv.counterparty?.name || '—'}</td>
                              <td>{Number(inv.amount||0).toFixed(2)} ₽</td>
                              <td>{renderImages(inv.images)}</td>
                              <td style={{width:260}} className="d-flex gap-2 align-items-center">
                                {typeof inv.imported_amount !== 'undefined' && Number(inv.imported_amount) > 0 && (
                                  <span className="small text-muted">
                                    Уже добавлено: {Number(inv.imported_amount).toFixed(2)} ₽
                                  </span>
                                )}
                                <Button size="sm" variant="outline-primary" onClick={()=>setImportingInvoice(inv)}>
                                  Добавить товары
                                </Button>
                                <Button size="sm" variant="outline-secondary" onClick={()=>setOcrInvoiceId(inv.id)}>
                                  Распознать фото
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    <div className="d-block d-md-none p-3">
                      {items.map(inv => (
                        <Card key={inv.id} className="mb-2">
                          <Card.Body>
                            <div className="small text-muted mb-1">{inv.date}</div>
                            <div className="fw-bold mb-1">{typeLabels[inv.type] || inv.type}</div>
                            <div className="small text-muted mb-1">
                              Контрагент: {inv.counterparty?.name || '—'}
                            </div>
                            <div className="mb-1">Сумма: {Number(inv.amount||0).toFixed(2)} ₽</div>
                            {typeof inv.imported_amount !== 'undefined' && Number(inv.imported_amount) > 0 && (
                              <div className="small text-muted mb-2">
                                Уже добавлено: {Number(inv.imported_amount).toFixed(2)} ₽
                              </div>
                            )}
                            {renderImages(inv.images)}
                            <div className="mt-2">
                              <Button size="sm" variant="outline-primary" onClick={()=>setImportingInvoice(inv)}>
                                Добавить товары
                              </Button>
                            <Button size="sm" variant="outline-secondary" className="ms-2" onClick={()=>setOcrInvoiceId(inv.id)}>
                              Распознать фото
                            </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              );
            })
          )
        )}
        <InvoiceImportModal
          show={!!importingInvoice}
          invoiceId={importingInvoice?.id}
          invoice={importingInvoice}
          onHide={()=>setImportingInvoice(null)}
          onImported={loadData}
        />
        <InvoiceOcrModal
          show={!!ocrInvoiceId}
          invoiceId={ocrInvoiceId}
          onHide={()=>setOcrInvoiceId(null)}
          onImported={loadData}
        />
      </Card.Body>
    </Card>
  );
};

export default InvoicesStorage;
